/**
 * **ENHANCED SIMULATION** — the one preference in this port that changes the
 * simulation rather than the picture, and the reasons it is its own setting,
 * its own row and its own default.
 *
 * With it on, escort sloops are put to sea wherever a sortie's region has open
 * water to hold one, and they fight: two 3-pounder high-angle mountings on
 * `flak.ts`'s own ballistics, two Maxims on the Zeppelin's own gunner model,
 * two searchlights, and a 4.7 in gun that trains and does not fire. The whole
 * of it is `docs/engineering/ship-sim.md`; the ship herself is
 * `ship-design.md`.
 *
 * ## Not `effects-settings.ts`, and this one is not a close call
 *
 * That file is *"port-extension **display** settings — visual effects the
 * original never drew"*, and every one of its seven toggles adds a picture that
 * nothing depends on. This adds a gun that can kill the player. `bail-settings.ts`
 * made exactly this argument for AD-021's BAIL OUT and it applies here with
 * more force: filing a warship's anti-aircraft battery under DISPLAY would say
 * it was cosmetic, and a pilot who switched DISPLAY off to get the 1990 picture
 * would find he had also disarmed the North Sea.
 *
 * So it lands in PREFERENCES' **FLYING** section, beside the other settings that
 * are neither a picture nor a volume.
 *
 * ## Off by default, which is the opposite of every DISPLAY row
 *
 * Those rows default **on** because a feature that ships default-off is
 * invisible out of the box. This one defaults **off**, and the reason is the
 * property the whole lane was built to keep: with it off the engine is
 * **bit-zero**. Not "close enough", not "a low gain" — every replay tape, every
 * seeded census and every recorded encounter hash in this repository is the
 * number it was before ships existed, because with the switch off no ship is
 * ever deployed, no stream is ever drawn from and `encounterHash` never sees a
 * field it did not see before (`packages/engine/src/combat/ship-deployment.ts`).
 * A default-on setting would have made that guarantee true and untested, since
 * nobody would ever have flown the off state.
 *
 * That also decides the stored-value fallback. `normalizeEffectsSettings` reads
 * `raw[key] !== false`, so a key that is merely absent lands on *today's*
 * default; this reads `raw.enhancedSimulation === true`, so a stored blob
 * written before this setting existed — and one written by a pilot who never
 * touched the row — reads off, for ever, whatever a later default becomes.
 *
 * ## It applies to the **next** sortie
 *
 * A sortie's ships are deployed once, when the mission's run is built against
 * the region's own water (`mission-spec.ts`'s `shipsFor`). Flipping the switch
 * mid-flight cannot add a ship to a fight already under way without breaking
 * lockstep and the replay tape both, so it does not try: the bit is stored at
 * once and the sea has ships in it from the next sortie.
 *
 * Same storage pattern as `bail-settings.ts` and `stick-settings.ts`, which is
 * the precedent for a one-field settings group: a small `SettingsStore`
 * round-trip, storage-agnostic so it is testable without a real `localStorage`.
 */

import type { SettingsStore } from "../audio/settings.ts";

export const SIM_SETTINGS_STORAGE_KEY = "red-opus/sim-settings";

export interface SimSettings {
  /** Whether the sortie's world carries the port's own simulated additions. Off means it does not exist. */
  readonly enhancedSimulation: boolean;
}

export const DEFAULT_SIM_SETTINGS: SimSettings = { enhancedSimulation: false };

/**
 * Reads whatever survives a `JSON.parse` of untrusted stored text.
 *
 * `=== true` rather than `!== false`, which is the opposite of every other
 * settings normalizer in this app and is the point: see the module note on why
 * a missing key must mean off.
 */
export function normalizeSimSettings(candidate: unknown): SimSettings {
  if (typeof candidate !== "object" || candidate === null) return DEFAULT_SIM_SETTINGS;
  const raw = candidate as Partial<Record<keyof SimSettings, unknown>>;
  return { enhancedSimulation: raw.enhancedSimulation === true };
}

export function serializeSimSettings(settings: SimSettings): string {
  return JSON.stringify(settings);
}

/** Defaults on missing key, corrupt JSON, or a non-object payload — never throws. */
export function loadSimSettings(store: SettingsStore): SimSettings {
  const raw = store.getItem(SIM_SETTINGS_STORAGE_KEY);
  if (raw === null) return DEFAULT_SIM_SETTINGS;
  try {
    return normalizeSimSettings(JSON.parse(raw));
  } catch {
    return DEFAULT_SIM_SETTINGS;
  }
}

export function saveSimSettings(store: SettingsStore, settings: SimSettings): void {
  store.setItem(SIM_SETTINGS_STORAGE_KEY, serializeSimSettings(settings));
}
